import React from "react";
import { Link } from "react-router-dom";

// CategoryBadge Component for reusability and dynamic styling
const CategoryBadge = ({ category, color }) => {
  const badgeStyle = {
    backgroundColor: color,
    padding: "5px 10px",
    borderRadius: "5px",
    marginRight: "10px",
    fontSize: "0.75rem",
    fontWeight: "700",
    color: "#fff",
  };

  return <div style={badgeStyle}>{category}</div>;
};

export default function BlogPageBox8() {
  return (
    <div className="blog-page-parent">
      {/* Date and Author Section */}
      <div className="blog-page-date-author">
        <span>Published</span>
        <span className="blog-page-date"> February 6, 2025</span>
        <span> by</span>
        <span className="blog-page-author"> FlexNet Team</span>
      </div>

      {/* Title Section */}
      <div className="blog-page-title">
        Security First: How FlexNet Protects Your Data and Operations
      </div>

      {/* Category Badges */}
      <div className="blog-page-categories">
        {[
          { name: "5G Security", color: "#6366F1" },
          { name: "Cybersecurity", color: "#22C55E" },
          { name: "Enterprise Solutions", color: "#F59E0B" },
        ].map((category, index) => (
          <CategoryBadge
            key={index}
            category={category.name}
            color={category.color}
          />
        ))}
      </div>

      {/* Blog Content */}
      <div className="blog-page-info">
        <h2>Introduction</h2>
        <p>
          In today’s digital landscape, security is not just a feature—it’s a
          necessity. As industries embrace private 5G networks, safeguarding
          sensitive data and ensuring operational continuity have become
          critical. FlexNet is built with Zero Trust principles and advanced
          security measures, offering uncompromising protection for your
          business. From microsegmentation to real-time alerts, FlexNet ensures
          your network remains secure, reliable, and future-ready.
        </p>
        <h2>Key Security Features in FlexNet</h2>
        <h3>1. Zero Trust Architecture (ZTA)</h3>
        <p>
          FlexNet enforces Zero Trust across your network, ensuring every
          device, user, and application is continuously verified.
        </p>
        <ul>
          <li>
            <strong>How It Works:</strong> Group-based microsegmentation
            restricts access to only authorized devices, isolating sensitive
            resources from potential threats.
          </li>
          <li>
            <strong>Why It Matters:</strong> This limits the impact of breaches
            by preventing unauthorized lateral movement within your network.
          </li>
        </ul>
        <h3>2. Firewall-Level Protection</h3>
        <p>
          FlexNet provides robust firewall-level security, safeguarding your
          private 4G/5G network against unauthorized access and external
          threats.
        </p>
        <ul>
          <li>
            <strong>Built-In Safeguards:</strong> End-to-end encryption, secure
            tunneling, and real-time traffic monitoring.
          </li>
        </ul>
        <h3>3. Anomaly Detection and Alerts via API</h3>
        <p>
          FlexNet integrates with your existing Security Information and Event
          Management (SIEM) systems to provide enhanced visibility into network
          activity.
        </p>
        <ul>
          <li>
            <strong>What It Offers:</strong> Alerts for unusual behavior, such
            as unauthorized access attempts or abnormal data usage.
          </li>
          <li>
            <strong>Partner-Friendly:</strong> Seamlessly integrate with
            third-party security solutions for centralized monitoring and faster
            response times.
          </li>
        </ul>
        <h3>4. Comprehensive Security Across 5G and Pre-Existing Networks</h3>
        <p>
          FlexNet doesn’t just secure your private 5G network—it also extends
          protection to your legacy systems, ensuring a unified security
          posture.
        </p>
        <h2>Enhanced Security with Microsegmentation</h2>
        <ul>
          <li>
            <strong>Group-Based Policies:</strong> Assign granular access
            permissions based on device groups, applications, or network zones.
          </li>
          <li>
            <strong>Dynamic Isolation:</strong> If a device shows suspicious
            activity, it can be automatically isolated without affecting the
            rest of the network.
          </li>
          <li>
            <strong>Real-World Example:</strong> In manufacturing, IoT devices
            can be segmented to ensure that a compromised sensor cannot affect
            critical production systems.
          </li>
        </ul>
        <h2>Seamless Integration for Holistic Security</h2>
        <ul>
          <li>
            <strong>SIEM Integration:</strong> Streamline your security
            operations by feeding alerts and logs into your existing systems for
            centralized control.
          </li>
          <li>
            <strong>Partner Security Solutions:</strong> FlexNet works
            seamlessly with third-party tools, strengthening your defense
            mechanisms with advanced analytics and automated responses.
          </li>
        </ul>
        <h2>Why Security Matters in Private 5G</h2>
        <ul>
          <li>
            <strong>Protect Sensitive Data:</strong> From manufacturing
            blueprints to customer data, FlexNet ensures your business-critical
            information is always secure.
          </li>
          <li>
            <strong>Ensure Operational Continuity:</strong> Prevent disruptions
            caused by breaches or unauthorized access to mission-critical
            systems.
          </li>
          <li>
            <strong>Build Customer Trust:</strong> In industries like
            automotive, logistics, and media, ensuring data integrity
            strengthens relationships with partners and customers alike.
          </li>
        </ul>
        <h2>FlexNet: No Compromises on Security</h2>
        <ul>
          <li>
            <strong>End-to-End Security:</strong> Built into the core of your
            network.
          </li>
          <li>
            <strong>Advanced Anomaly Detection:</strong> Real-time protection
            against threats.
          </li>
          <li>
            <strong>Zero Trust Architecture:</strong> Safeguards against both
            external and internal threats.
          </li>
          <li>
            <strong>Seamless Integration:</strong> Works with third-party
            security solutions for a unified defense.
          </li>
        </ul>
        <h2>Conclusion</h2>
        <p>
          In a world where cyber threats are constantly evolving, FlexNet offers
          peace of mind. Our commitment to Zero Trust and microsegmentation
          ensures your network is protected at every level, enabling your
          business to grow securely and confidently.
        </p>
        <h2>Ready to Strengthen Your Security with FlexNet?</h2>
        <p className="font-bold text-lg">
          <Link to="/contact-us">
            <span className="text-blue-600 mr-1">Learn More</span>
          </Link>
          or
          <Link to="/connectivity">
            <span className="text-blue-600 mr-1 ml-1">
              Contact Us for a Demo
            </span>
            today to experience uncompromising protection for your private 5G
            network.
          </Link>
        </p>
      </div>
    </div>
  );
}
